import { Component, Input, OnInit } from '@angular/core';
import { IOrganisationInteraction } from '../../interfaces/interactions/organisation-interaction';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EventService, FileStorageService, OrganisationInteractionService } from '../../services';
import { ConfirmationService, MessageService, SelectItem } from 'primeng';
import { InteractionEventTypeEnum } from '../../interfaces/interactions/interaction-event';
import { Message } from '../../classes/Message';
import { NgxFileDropEntry } from 'ngx-file-drop';

@Component({
  selector: 'interactions',
  styleUrls: ['./interactions.component.scss'],
  templateUrl: './interactions.component.html',
})
export class InteractionsComponent implements OnInit {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private interactionService: OrganisationInteractionService,
    private fileStorageService: FileStorageService,
    private eventService: EventService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  @Input() noAdd: boolean;

  _organisationId: number;
  @Input('organisationId')
  set organisationId(value: number) {
    this._organisationId = value;
    this.loadInteractions();
  }

  get organisationId(): number {
    return this._organisationId;
  }

  private popupPhotoUrl: string;
  public files: any;
  public fileToDelete: any;

  interactions: IOrganisationInteraction[] = [];
  selectedInteraction: IOrganisationInteraction;

  channels: SelectItem[];
  categories: SelectItem[];
  departments: SelectItem[];

  form: UntypedFormGroup;
  showDialog = false;
  loading = false;
  editing = false;

  isShownPopupPhoto: boolean = false;
  loadingPopupPhoto: boolean = false;
  savingAttachment = false;

  ngOnInit() {
    this.form = this.formBuilder.group({
      addedDate: ['', Validators.required],
      interactionChannelId: [null, Validators.required],
      interactionCategoryId: [null, Validators.required],
      interactionDepartmentId: [null, Validators.required],
      jobId: [null],
      note: ['', Validators.required],
      id: [null],
      photographLocation: [null],
    });

    this.loadCategories();
    this.loadChannels();
    this.loadDepartments();
  }

  get f() {
    return this.form.controls;
  }

  loadCategories() {
    this.interactionService.getInteractionCategories(true).then((res) => {
      //console.log('Interaction categories: ', res);

      this.categories = res.map((x) => {
        return { label: x.name, value: x.id };
      });

      this.categories.unshift({ label: 'Select Category', value: null });
    });
  }

  loadChannels() {
    this.interactionService.getInteractionChannels().then((res) => {
      this.channels = res.map((x) => {
        return { label: x.name, value: x.id };
      });

      this.channels.unshift({ label: 'Select Channel', value: null });
    });
  }

  loadDepartments() {
    this.interactionService.getInteractionDepartments().then((res) => {
      this.departments = res.map((x) => {
        return { label: x.name, value: x.id };
      });

      this.departments.unshift({ label: 'Select Department', value: null });
    });
  }

  loadInteractions() {
    this.showDialog = false;
    this.loading = true;
    this.interactionService
      .getInteractions(this.organisationId)
      .then((result) => {
        this.interactions = result;

        this.interactions.forEach((item) => {
          const wl = this.interactionService.getWarningLevel(item.addedDate);
          item.warningLevel = wl;
        });

        this.loading = false;
      })
      .catch((err) => {
        this.loading = false;
      });
  }

  addInteraction() {
    this.editing = false;

    this.form.patchValue({ addedDate: new Date() });
    this.form.get('interactionChannelId').setValue(null);
    this.form.get('interactionCategoryId').setValue(null);
    this.form.get('jobId').setValue(null);
    this.form.get('note').setValue('');
    this.form.get('id').setValue(null);
    this.form.get('photographLocation').setValue(null);

    this.selectedInteraction = null;
    this.files = [];
    this.fileToDelete = null;

    this.showInteractionDialog();
  }

  showInteractionDialog() {
    // raise event to clear search text in jobRef component
    this.eventService.InteractionEvent.emit({
      eventType: InteractionEventTypeEnum.ResetJobSearchText,
    });
    this.showDialog = true;
  }

  async saveInteraction() {
    if (!this.form.valid) {
      return;
    }

    this.savingAttachment = true;

    if (this.files?.length > 0) {
      console.log('FILES ', this.files);

      const fileEntry = this.files[0].fileEntry as FileSystemFileEntry;

      if (fileEntry != null) {
        const file: File = await new Promise<File>((resolve) => {
          fileEntry.file((file: File) => resolve(file));
        });

        const res = await this.fileStorageService.uploadFile(file);
        if (res.successfully) {
          this.form.get('photographLocation').setValue(res.fileData.guid);
          this.files = {
            photographLocation: res.fileData.guid,
            fileName: file.name,
          };
        } else {
          this.messageService.add(Message.Error('File could not be uploaded'));
        }
      }
    }

    if (this.fileToDelete) {
      await this.fileStorageService.deleteFile(this.fileToDelete).then((res) => {
        if (res) {
          this.files = this.files.filter((x) => x.photographLocation != this.fileToDelete);
          this.form.get('photographLocation').setValue(null);
        }
      });
    }

    this.savingAttachment = false;

    const interaction = <IOrganisationInteraction>{
      ...this.form.value,
      organisationId: this.organisationId,
    };

    // Convert the form value date to utc
    interaction.addedDate = new Date(
      Date.UTC(
        interaction.addedDate.getFullYear(),
        interaction.addedDate.getMonth(),
        interaction.addedDate.getDate(),
        interaction.addedDate.getHours(),
        interaction.addedDate.getMinutes(),
      ),
    );

    if (this.editing) {
      this.interactionService.updateInteraction(interaction).then((result) => {
        this.loadInteractions();
      });
    } else {
      this.interactionService.addInteraction(interaction).then((result) => {
        this.loadInteractions();
      });
    }
  }

  closeDialog() {
    if (this.savingAttachment) {
      return;
    }
    this.showDialog = false;
  }

  editInteraction(interaction: IOrganisationInteraction) {
    this.editing = true;

    this.selectedInteraction = interaction;
    let dt = new Date(interaction.addedDate);

    this.form.get('addedDate').setValue(dt);
    this.form.get('interactionChannelId').setValue(interaction.interactionChannelId);
    this.form.get('interactionCategoryId').setValue(interaction.interactionCategoryId);
    this.form.get('interactionDepartmentId').setValue(interaction.interactionDepartmentId);
    this.form.get('jobId').setValue(interaction.jobId);
    this.form.get('note').setValue(interaction.note);
    this.form.get('id').setValue(interaction.id);
    this.form.get('photographLocation').setValue(interaction.photographLocation);

    this.files = [];
    if (interaction.photographLocation) {
      this.files = [
        {
          photographLocation: interaction.photographLocation,
          fileName: interaction.photographLocation,
        },
      ];
    }

    this.fileToDelete = null;

    this.showInteractionDialog();
  }

  openPhotoPresigned(photoUrl) {
    if (!photoUrl) {
      return;
    }

    this.loadingPopupPhoto = true;

    this.fileStorageService.getPreSignedURLs([photoUrl]).then((url) => {
      this.popupPhotoUrl = url.length ? url[0] : null;
      this.isShownPopupPhoto = true;
      this.loadingPopupPhoto = false;
    });
  }

  onJobRefSelected(jobRefId) {
    this.f.jobId.setValue(jobRefId);

    console.log('FORM VALUES: ', this.form.value);
  }

  pinInteraction(item: IOrganisationInteraction) {
    // if we're trying to pin an interaction, check there aren't already max amount pinned
    if (
      !item.pinnedOrder &&
      this.interactions.reduce((total, x) => total + (x.pinnedOrder ? 1 : 0), 0) >= 3
    ) {
      this.messageService.add(
        Message.Error('You can only pin a maximum of 3 interactions.', 'Error'),
      );
      return;
    }

    // make the request to perform the same function in the API
    this.interactionService.pinInteraction(item.id).then(() => {
      this.loadInteractions();
    });
  }

  dropped(files: NgxFileDropEntry[]) {
    if (files && files.length > 1) {
      this.messageService.add(Message.Error('Only one attachment allowed per interaction'));
      return;
    }

    let droppedFile = files[0];

    // Is it a file?
    if (droppedFile.fileEntry.isFile) {
      // set file to be saved when interaction is saved
      this.files = [
        {
          fileEntry: droppedFile.fileEntry,
          fileName: droppedFile.fileEntry.name,
        },
      ];
    }
  }

  fileOver($event: any) {}

  fileLeave($event: any) {}

  removeFile(event, file) {
    event.preventDefault();
    event.stopPropagation();

    this.confirmationService.confirm({
      message: `Are you sure you want to delete the image?`,
      acceptLabel: 'Continue',
      rejectLabel: 'Cancel',
      accept: () => {
        // don't add to delete list if file not yet saved
        if (!file.fileEntry) {
          this.fileToDelete = file.photographLocation;
        }

        this.files = this.files.filter((x) => x.fileName != file.fileName);
      },
    });
  }
}
